import React, { useState, useEffect, useRef } from 'react';
import TaskDropdown from './taskDropdown';
import Wochenplan from './wochenplan';
import { apiFetch } from './api';

const Zeiterfassung = ({ user }) => {
  const [name, setName] = useState('');
  const [taskId, setTaskId] = useState(null);
  const [taskName, setTaskName] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [startZeit, setStartZeit] = useState(null);
  const [verstrichen, setVerstrichen] = useState(0);
  const [eintraege, setEintraege] = useState([]);
  const [reloadSignal, setReloadSignal] = useState(0);
  const intervalRef = useRef(null);

  // Edit-Popup State
  const [editPopup, setEditPopup] = useState(null);
  const [editName, setEditName] = useState('');
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editTaskId, setEditTaskId] = useState(null);
  const [editTaskName, setEditTaskName] = useState('');

  useEffect(() => {
    const gespeichert = window.localStorage.getItem('tracker');
    if (gespeichert) {
      const t = JSON.parse(gespeichert);
      setName(t.name || '');
      setTaskId(t.taskId || null);
      setTaskName(t.taskName || '');
      setStartZeit(t.startZeit);
      setLaeuft(true);
    }
    ladeEintraege();
  }, []);

  useEffect(() => {
    if (laeuft && startZeit) {
      intervalRef.current = setInterval(() => {
        setVerstrichen(Math.floor((Date.now() - new Date(startZeit).getTime()) / 1000));
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [laeuft, startZeit]);

  const ladeEintraege = async () => {
    const heuteVon = new Date(); heuteVon.setHours(0, 0, 0, 0);
    const heuteBis = new Date(); heuteBis.setHours(23, 59, 59, 999);
    const res = await apiFetch(
      `/zeit/eintraege?user_id=${user.id}&von=${heuteVon.toISOString()}&bis=${heuteBis.toISOString()}`
    );
    if (!res) return;
    const data = await res.json();
    setEintraege(Array.isArray(data) ? data : []);
  };

  const start = () => {
    if (!taskId && !name) { alert('Bitte zuerst eine Task auswählen oder eine Beschreibung eingeben'); return; }
    const jetzt = new Date().toISOString();
    setStartZeit(jetzt);
    setLaeuft(true);
    setVerstrichen(0);
    window.localStorage.setItem('tracker', JSON.stringify({ name, taskId, taskName, startZeit: jetzt }));
  };

  const stop = async () => {
    const endZeit = new Date().toISOString();
    setLaeuft(false);
    clearInterval(intervalRef.current);
    await apiFetch('/zeit/eintraege', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, task_id: taskId || null, user_id: user.id, start_zeit: startZeit, end_zeit: endZeit })
    });
    window.localStorage.removeItem('tracker');
    setName(''); setTaskId(null); setTaskName('');
    setVerstrichen(0); setStartZeit(null);
    ladeEintraege();
    setReloadSignal(s => s + 1);
  };

  const eintragLoeschen = async (id) => {
    await apiFetch(`/zeit/eintraege/${id}`, { method: 'DELETE' });
    setEditPopup(null);
    ladeEintraege();
    setReloadSignal(s => s + 1);
  };

  const editOeffnen = (e) => {
    setEditPopup(e);
    setEditName(e.name || '');
    setEditStart(toLocalInput(new Date(e.start_zeit)));
    setEditEnd(toLocalInput(new Date(e.end_zeit)));
    setEditTaskId(e.task_id || null);
    setEditTaskName(e.task_name || '');
  };

  const editSpeichern = async () => {
    await apiFetch(`/zeit/eintraege/${editPopup.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: editName,
        task_id: editTaskId || null,
        start_zeit: new Date(editStart).toISOString(),
        end_zeit: new Date(editEnd).toISOString()
      })
    });
    setEditPopup(null);
    ladeEintraege();
    setReloadSignal(s => s + 1);
  };

  const toLocalInput = (d) => {
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  const formatTimer = (sek) => {
    const h = String(Math.floor(sek / 3600)).padStart(2, '0');
    const m = String(Math.floor((sek % 3600) / 60)).padStart(2, '0');
    const s = String(sek % 60).padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const formatMin = (min) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (h === 0) return `${m}min`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}min`;
  };

  const formatUhrzeit = (iso) => {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const labelStyle = {
    display: 'block', fontSize: '11px', fontWeight: '500', color: '#6b6b6b',
    marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'
  };
  const inputStyle = {
    width: '100%', padding: '9px 12px', fontSize: '13px', border: '1px solid #e8e8e8',
    borderRadius: '8px', outline: 'none', boxSizing: 'border-box'
  };

  return (
    <div style={{ padding: '40px' }}>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 6px 0', letterSpacing: '-0.5px' }}>
          Zeiterfassung
        </h2>
        <p style={{ fontSize: '14px', color: '#9b9b9b', margin: 0 }}>Tracke deine Zeit auf Tasks</p>
      </div>

      {/* Live-Tracker */}
      <div style={{
        backgroundColor: '#ffffff', border: '1px solid #efefef', borderRadius: '12px',
        padding: '24px', marginBottom: '32px',
        display: 'flex', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap'
      }}>
        <div style={{ flex: '1 1 200px' }}>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: '#6b6b6b', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Woran arbeitest du?
          </label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Beschreibung (Pflicht wenn keine Task)"
            style={{ width: '100%', padding: '10px 14px', fontSize: '14px', border: '1px solid #e8e8e8', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ flex: '1 1 220px' }}>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: '#6b6b6b', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Task (optional)
          </label>
          <TaskDropdown selectedTaskId={taskId} selectedTaskName={taskName}
            onSelect={(id, tname) => { setTaskId(id); setTaskName(tname); }}
          />
        </div>

        <div style={{
          fontSize: '24px', fontWeight: '700', color: '#1a1a1a', fontVariantNumeric: 'tabular-nums',
          letterSpacing: '0px', minWidth: '110px', textAlign: 'center', paddingBottom: '8px'
        }}>
          {formatTimer(verstrichen)}
        </div>

        {!laeuft ? (
          <button onClick={start} style={{
            padding: '11px 28px', backgroundColor: '#1a1a1a', color: '#fff', border: 'none',
            borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
          }}>Start</button>
        ) : (
          <button onClick={stop} style={{
            padding: '11px 28px', backgroundColor: '#cc0000', color: '#fff', border: 'none',
            borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
          }}>Stop</button>
        )}
      </div>

      {/* Heutige Einträge */}
      <div>
        <p style={{ fontSize: '11px', fontWeight: '500', color: '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
          Heute
        </p>
        {eintraege.length === 0 ? (
          <p style={{ fontSize: '14px', color: '#c4c4c4' }}>Noch keine Einträge heute.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {eintraege.map((e) => {
              const ohneTask = !e.task_id;
              return (
                <div key={e.id} onClick={() => editOeffnen(e)} style={{
                  backgroundColor: ohneTask ? '#fafafa' : '#ffffff',
                  borderRadius: '10px',
                  padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  border: ohneTask ? '1px dashed #d4d4d4' : '1px solid #efefef',
                  cursor: 'pointer',
                  borderLeft: ohneTask ? '4px dashed #d4d4d4' : e.mutter_farbe ? `4px solid ${e.mutter_farbe}` : '1px solid #efefef',
                }}>
                  <div>
                    <p style={{ fontSize: '14px', fontWeight: '600', color: ohneTask ? '#9b9b9b' : '#1a1a1a', margin: '0 0 2px 0' }}>
                      {e.name || e.task_name || '– kein Name –'}
                      {ohneTask && <span style={{ fontSize: '11px', fontWeight: '400', color: '#c4c4c4', marginLeft: '8px' }}>Keine Task zugewiesen</span>}
                    </p>
                    <p style={{ fontSize: '12px', color: '#9b9b9b', margin: 0 }}>
                      {e.mutter_name ? `${e.mutter_name} · ` : ''}{e.projekt_name ? `${e.projekt_name} · ` : ''}{e.task_name || ''}{e.task_name ? ' · ' : ''}{formatUhrzeit(e.start_zeit)}–{formatUhrzeit(e.end_zeit)}
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>{formatMin(e.minuten)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Wochenplan user={user} reloadSignal={reloadSignal} onReload={() => { ladeEintraege(); setReloadSignal(s => s + 1); }} />

      {/* Edit-Popup */}
      {editPopup && (
        <div onClick={() => setEditPopup(null)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.35)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: '#ffffff', borderRadius: '14px', width: '100%', maxWidth: '420px',
            padding: '32px', boxShadow: '0 12px 48px rgba(0,0,0,0.18)',
            borderTop: editPopup.mutter_farbe ? `4px solid ${editPopup.mutter_farbe}` : '4px solid #e8e8e8'
          }}>
            <p style={{ fontSize: '11px', fontWeight: '500', color: editPopup.mutter_farbe || '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 4px 0' }}>
              {editPopup.mutter_name || editPopup.projekt_name || 'Kein Bereich'}
            </p>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 24px 0', letterSpacing: '-0.3px' }}>
              {editPopup.task_name || editPopup.name || '– kein Name –'}
            </h3>

            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Beschreibung</label>
              <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} style={inputStyle} />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Task (optional)</label>
              <TaskDropdown
                selectedTaskId={editTaskId}
                selectedTaskName={editTaskName}
                onSelect={(id, tname) => { setEditTaskId(id); setEditTaskName(tname); }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '24px' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Von</label>
                <input type="datetime-local" value={editStart} onChange={(e) => setEditStart(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Bis</label>
                <input type="datetime-local" value={editEnd} onChange={(e) => setEditEnd(e.target.value)} style={inputStyle} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={editSpeichern} style={{
                padding: '10px 20px', backgroundColor: '#1a1a1a', color: '#fff', border: 'none',
                borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
              }}>Speichern</button>
              <button onClick={() => eintragLoeschen(editPopup.id)} style={{
                padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid #ffdddd',
                borderRadius: '8px', fontSize: '13px', color: '#cc0000', cursor: 'pointer'
              }}>Löschen</button>
              <button onClick={() => setEditPopup(null)} style={{
                padding: '10px 20px', backgroundColor: 'transparent', color: '#6b6b6b',
                border: '1px solid #e8e8e8', borderRadius: '8px', fontSize: '13px', cursor: 'pointer'
              }}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Zeiterfassung;